import React, { useCallback, useMemo, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE, ROUTES } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { formatDateTime } from '@/shared/lib/format';
import { highlightText } from '@/shared/lib/text';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import type { ModelVersionDetail } from '@/entities/model-version';
import { useAvailableTags } from '@/entities/tags';

import { SEARCH_PLACEHOLDERS } from '@/widgets/asset-detail/shared/constants';

import styles from './VersionsTab.module.css';

const COLUMN_WIDTHS = {
  VERSION: 120,
  DESCRIPTION: 300,
  TAGS: 250,
  ALIASES: 250,
  REGISTERED_AT: 180,
  CREATED_BY: 200,
} as const;

type VersionsTabProps = {
  versions: ModelVersionDetail[];
  modelFqn: string;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
};

const VersionsTabComponent: React.FC<VersionsTabProps> = ({
  versions,
  modelFqn,
  isLoading = false,
  isError = false,
  error,
  onRetry,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);
  const { data: availableTags } = useAvailableTags();

  const availableTagFQNs = useMemo(() => new Set(availableTags?.map((t) => t.value) || []), [availableTags]);

  const handleTagClick = useCallback(
    (tagFQN: string) => {
      navigate(`${ROUTES.TAGS_DETAIL}/${encodeURIComponent(tagFQN)}`);
    },
    [navigate]
  );

  const filteredVersions = useMemo(() => {
    if (!searchQuery || searchQuery.length === 0) return versions;

    const lowerQuery = searchQuery.toLowerCase();
    return versions.filter((version) => {
      const versionMatch = version.version.toLowerCase().includes(lowerQuery);
      const userMatch = version.user_id?.toLowerCase().includes(lowerQuery);
      const descriptionMatch = version.description?.toLowerCase().includes(lowerQuery);
      const aliasMatch = version.aliases?.some((alias) => alias.toLowerCase().includes(lowerQuery));
      const tagMatch = version.tags?.some(
        (tag) => tag.key.toLowerCase().includes(lowerQuery) || tag.value.toLowerCase().includes(lowerQuery)
      );

      return versionMatch || userMatch || descriptionMatch || aliasMatch || tagMatch;
    });
  }, [searchQuery, versions]);

  const hasData = filteredVersions.length > 0;

  const columns: ColumnsType<ModelVersionDetail> = useMemo(() => {
    const baseColumns: ColumnsType<ModelVersionDetail> = [
      {
        title: '버전',
        dataIndex: 'version',
        key: 'version',
        width: COLUMN_WIDTHS.VERSION,
        fixed: 'left',
        render: (version: string) => (
          <Link to={`/mlmodel/${modelFqn}/versions/${version}`}>
            Version {highlightText(version, searchQuery, { className: 'highlight' })}
          </Link>
        ),
        sorter: (a, b) => {
          const aNum = parseInt(a.version, 10);
          const bNum = parseInt(b.version, 10);
          return bNum - aNum;
        },
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: hasData ? COLUMN_WIDTHS.DESCRIPTION : 'auto',
        render: (description?: string) => {
          return <AssetDescription description={description} mode="expandable" highlightQuery={searchQuery.trim()} />;
        },
      },
      {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: COLUMN_WIDTHS.TAGS,
        render: (tags: ModelVersionDetail['tags']) => {
          if (!tags || tags.length === 0) return '-';

          const badgeItems = tags
            .filter((tag) => {
              const key = tag.key.toLowerCase();
              return !key.includes('tier');
            })
            .map((tag) => {
              let displayName = tag.value || tag.key;

              if (!tag.value && availableTags) {
                const matchedTag = availableTags.find((availableTag) => availableTag.value === tag.key);
                if (matchedTag) {
                  displayName = matchedTag.label;
                }
              }

              return {
                tagFQN: tag.key,
                name: tag.key,
                displayName,
              };
            });

          if (badgeItems.length === 0) return '-';

          const disabledItems = new Set<string>();
          badgeItems.forEach((item) => {
            if (!availableTagFQNs.has(item.tagFQN)) {
              disabledItems.add(item.tagFQN);
            }
          });

          return (
            <BadgeList
              highlightQuery={searchQuery}
              items={badgeItems}
              maxVisibleCount={5}
              isEllipsis={false}
              onItemClick={handleTagClick}
              disabledItems={disabledItems}
            />
          );
        },
      },
      {
        title: '별칭',
        dataIndex: 'aliases',
        key: 'aliases',
        width: COLUMN_WIDTHS.ALIASES,
        render: (aliases?: string[]) => {
          if (!aliases || aliases.length === 0) return '-';
          const badgeItems = aliases.map((alias) => ({
            tagFQN: alias,
            name: alias,
            displayName: `@${alias}`,
          }));
          return (
            <BadgeList
              highlightQuery={searchQuery}
              items={badgeItems}
              maxVisibleCount={3}
              showIcon={false}
              showTooltip
            />
          );
        },
      },
    ];

    if (showOwners) {
      baseColumns.push({
        title: '생성자',
        dataIndex: 'user_id',
        key: 'user_id',
        width: COLUMN_WIDTHS.CREATED_BY,
        render: (userId: string) => highlightText(userId || '-', searchQuery, { className: 'highlight' }),
      });
    }

    baseColumns.push({
      title: '등록일',
      dataIndex: 'creation_timestamp',
      key: 'creation_timestamp',
      width: COLUMN_WIDTHS.REGISTERED_AT,
      render: (timestamp: number) => formatDateTime(timestamp),
      sorter: (a, b) => a.creation_timestamp - b.creation_timestamp,
    });

    return baseColumns;
  }, [hasData, modelFqn, showOwners, availableTags, availableTagFQNs, handleTagClick, searchQuery]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredVersions.length,
    viewportOffset: 648,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="버전 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.versionsTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.VERSIONS || '버전 검색...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredVersions}
        rowKey={(record) => `${record.name}-${record.version}`}
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: filteredVersions.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
            />
          ),
        }}
        className={styles.versionsTable}
      />
    </div>
  );
};

export const VersionsTab = React.memo(VersionsTabComponent);
